import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserProfile } from '@/contexts/user.context';
import { UserRole } from '@/lib/types';
import { organizationsQueryOptions } from '@/lib/query-options';
import { SchedulerJobsTable } from '@/components/admin/scheduler/scheduler-jobs-table';
import { OrgClockManager } from '@/components/admin/scheduler/org-clock-manager';

export const Route = createFileRoute('/_authenticated/_layout/admin/scheduler')({
  component: SchedulerAdminPage,
});

const ALL_ORGS = 'all';

function SchedulerAdminPage() {
  const { user } = useUserProfile();
  const { data: orgs } = useQuery(organizationsQueryOptions());
  const [orgFilter, setOrgFilter] = useState<string>(ALL_ORGS);

  if (!user || user.role !== UserRole.ADMIN) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="flex items-center gap-2 rounded-lg border border-l-4 border-l-[#B91C1C] bg-[#FEE2E2] px-4 py-3 text-sm text-[#B91C1C]">
          <AlertCircle className="h-4 w-4" />
          Access denied. This page is for administrators.
        </div>
      </div>
    );
  }

  const selectedOrgId = orgFilter === ALL_ORGS ? null : Number(orgFilter);
  const selectedOrg = orgs?.find((o) => o.id === selectedOrgId);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Scheduler</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage scheduled jobs, per-organization clocks, and manual runs.
        </p>
      </div>

      <div className="flex items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-gray-500">Organization</Label>
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All organizations" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL_ORGS}>All organizations</SelectItem>
              {orgs?.map((o) => (
                <SelectItem key={o.id} value={String(o.id)}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedOrgId != null && selectedOrg && (
        <OrgClockManager organizationId={selectedOrgId} organizationName={selectedOrg.name} />
      )}

      <SchedulerJobsTable organizationId={selectedOrgId} />
    </div>
  );
}
